import { useMutationWithToast } from '@/hooks/useMutationWithToast'
import { createEnrollment, createExtension } from '@fintree/supabase/enrollments'
import { searchStudentByPhone } from '@fintree/supabase/students'
import { consolidateStudentPhone } from '@fintree/supabase/students'
import { QK } from '@/lib/queryKeys'
import type { ActivityActor } from '@fintree/supabase/shared'
import type { CreateEnrollmentPayload } from '@fintree/supabase/enrollments'
import type { CreateExtensionPayload } from '@fintree/supabase/enrollments'

/* ------------------------------------------------------------------ */
/* Create enrollment                                                    */
/* ------------------------------------------------------------------ */

export function useCreateEnrollment(
  actor: ActivityActor,
  options?: {
    onSuccess?: (data: unknown, variables: CreateEnrollmentPayload) => void
  }
) {
  return useMutationWithToast({
    mutationFn: (payload: CreateEnrollmentPayload) => createEnrollment(payload, actor),
    successMessage: 'Enrollment created successfully',
    onSuccess: options?.onSuccess,
  })
}

/* ------------------------------------------------------------------ */
/* Create extension (enrollment change)                                 */
/* ------------------------------------------------------------------ */

export function useCreateExtension(
  actor: ActivityActor,
  options?: {
    onSuccess?: (data: unknown, variables: CreateExtensionPayload) => void
  }
) {
  return useMutationWithToast({
    mutationFn: (payload: CreateExtensionPayload) => createExtension(payload, actor),
    successMessage: 'Enrollment change saved',
    invalidateKeys: [() => QK.studentExtensions(''), () => QK.studentEnrollments('')],
    onSuccess: options?.onSuccess,
  })
}

/* ------------------------------------------------------------------ */
/* Search student by phone (used as mutation for duplicate check)       */
/* ------------------------------------------------------------------ */

export function useSearchStudentByPhone(
  options?: {
    onSuccess?: (data: Awaited<ReturnType<typeof searchStudentByPhone>>, variables: string) => void
  }
) {
  return useMutationWithToast({
    mutationFn: (phone: string) => searchStudentByPhone(phone),
    successMessage: '',
    onSuccess: options?.onSuccess,
  })
}

export function useConsolidateStudentPhone(
  actor: ActivityActor,
  options?: {
    onSuccess?: (data: Awaited<ReturnType<typeof consolidateStudentPhone>>, variables: { oldPhone: string; newPhone: string; reason?: string }) => void
  }
) {
  return useMutationWithToast({
    mutationFn: ({ oldPhone, newPhone, reason }: { oldPhone: string; newPhone: string; reason?: string }) =>
      consolidateStudentPhone(oldPhone, newPhone, actor, { reason }),
    successMessage: 'Student records consolidated successfully',
    invalidateKeys: [() => QK.studentByPhone(''), () => QK.studentsBase()],
    onSuccess: options?.onSuccess,
  })
}
